import { Delete, Edit, History } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import apiClient from '../../services/api';

type HealthStatus = 'normal' | 'sick' | 'injured';

interface StatusHistoryEntry {
  status: HealthStatus;
  startDate: string;
  endDate: string | null;
  durationDays?: number;
}

interface HealthStatusProps {
  initialStatus?: HealthStatus;
  initialHistory?: StatusHistoryEntry[];
  onStatusChange?: (shouldSyncToIntervals: boolean, newStatus?: HealthStatus) => void;
}

const HealthStatusWidget: React.FC<HealthStatusProps> = ({
  initialStatus,
  initialHistory,
  onStatusChange,
}) => {
  const [currentStatus, setCurrentStatus] = useState<HealthStatus>(initialStatus || 'normal');
  const [statusHistory, setStatusHistory] = useState<StatusHistoryEntry[]>(initialHistory || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<HealthStatus | null>(null);
  const [adjustmentInfo, setAdjustmentInfo] = useState<string | null>(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [refreshingHistory, setRefreshingHistory] = useState(false);
  const [editStartDate, setEditStartDate] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Update status when initialStatus prop changes
  useEffect(() => {
    if (initialStatus) {
      setCurrentStatus(initialStatus);
    }
  }, [initialStatus]);

  // Update history when initialHistory prop changes
  useEffect(() => {
    if (initialHistory) {
      setStatusHistory(initialHistory);
    }
  }, [initialHistory]);

  // Load current status on mount only if not provided via prop
  useEffect(() => {
    if (!initialStatus || !initialHistory) {
      loadHealthStatus();
    }
  }, [initialStatus, initialHistory]);

  const loadHealthStatus = async () => {
    try {
      const response = await apiClient.getHealthStatus();
      if (response.success && response.data) {
        setCurrentStatus(response.data.healthStatus);
        setStatusHistory(response.data.statusHistory || []);
      }
    } catch (err: any) {
      console.error('Failed to load health status:', err);
      // Don't show error, just default to normal
      setCurrentStatus('normal');
    }
  };

  const refreshHistoryData = async () => {
    setRefreshingHistory(true);
    try {
      const response = await apiClient.getHealthStatus();
      if (response.success && response.data) {
        setStatusHistory(response.data.statusHistory || []);
      }
    } catch (err: any) {
      console.error('Failed to refresh health status history:', err);
    } finally {
      setRefreshingHistory(false);
    }
  };

  const handleOpenHistory = () => {
    setHistoryModalOpen(true);
    // Refresh history data when opening modal to ensure it's up-to-date
    refreshHistoryData();
  };

  const handleStatusClick = (status: HealthStatus) => {
    if (status === currentStatus) return;

    setPendingStatus(status);
    setConfirmDialogOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!pendingStatus) return;

    setLoading(true);
    setError(null);
    setAdjustmentInfo(null);

    try {
      const response = await apiClient.updateHealthStatus(pendingStatus);

      if (response.success && response.data) {
        setCurrentStatus(response.data.healthStatus);
        setStatusHistory(response.data.statusHistory || []);

        // Show adjustment info if returning to normal
        if (pendingStatus === 'normal' && response.data.adjustmentInfo) {
          const info = response.data.adjustmentInfo;
          let message = 'Your training plan has been adjusted for recovery.';

          if (info.sickDurationDays) {
            message = `Welcome back! You were ${currentStatus} for ${info.sickDurationDays} day(s). `;

            if (info.workoutsCancelled && info.workoutsCancelled > 0) {
              message += `${info.workoutsCancelled} workout(s) during your sick period have been cancelled. `;
            }

            if (info.adjustmentType === 'short') {
              message += `Your next 2 training days have been reduced to 70% to ease you back in.`;
            } else if (info.adjustmentType === 'long') {
              message += `Your training has been adjusted from today onwards to help you return safely.`;
            }

            if (info.daysAffected) {
              message += ` ${info.daysAffected} workout(s) adjusted for recovery.`;
            }
          }

          setAdjustmentInfo(message);
        } else if (pendingStatus === 'sick' || pendingStatus === 'injured') {
          setAdjustmentInfo(`Today's workout has been cancelled. Take care and get well soon! 🏥`);
        }

        // Notify parent component to refresh training plan and sync to Intervals.icu if needed
        const shouldSyncToIntervals = (response.data.adjustmentInfo as any)?.planUpdated === true;
        if (onStatusChange) {
          onStatusChange(shouldSyncToIntervals, response.data.healthStatus);
        }
      } else {
        setError(response.message || 'Failed to update health status');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while updating health status');
    } finally {
      setLoading(false);
      setConfirmDialogOpen(false);
      setPendingStatus(null);
    }
  };

  const handleCancelDialog = () => {
    setConfirmDialogOpen(false);
    setPendingStatus(null);
  };

  const handleEditStartDate = () => {
    const mostRecent = statusHistory[statusHistory.length - 1];
    if (mostRecent && !mostRecent.endDate) {
      setEditStartDate(mostRecent.startDate.split('T')[0]); // Get YYYY-MM-DD format
      setEditDialogOpen(true);
    }
  };

  const handleConfirmEditStartDate = async () => {
    if (!editStartDate) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.updateHealthStatus(currentStatus, editStartDate);

      if (response.success && response.data) {
        setCurrentStatus(response.data.healthStatus);
        setStatusHistory(response.data.statusHistory || []);
        setEditDialogOpen(false);
        setAdjustmentInfo('Start date updated successfully');

        // Force re-render by reloading from backend
        await loadHealthStatus();
      } else {
        setError(response.message || 'Failed to update start date');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while updating start date');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMostRecent = () => {
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    setError(null);

    try {
      // Set status to normal without a date to trigger deletion of most recent entry
      const response = await apiClient.updateHealthStatus('normal', 'delete-current');

      if (response.success && response.data) {
        setCurrentStatus(response.data.healthStatus);
        setStatusHistory(response.data.statusHistory || []);
        setDeleteDialogOpen(false);
        setHistoryModalOpen(false);
        setAdjustmentInfo('Most recent status entry deleted');

        // Force re-render by reloading from backend
        await loadHealthStatus();

        // Notify parent about status change (no need to sync to intervals for delete)
        if (onStatusChange) {
          onStatusChange(false);
        }
      } else {
        setError(response.message || 'Failed to delete status entry');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while deleting status entry');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: HealthStatus): 'success' | 'warning' | 'error' | 'primary' => {
    switch (status) {
      case 'normal':
        return 'success';
      case 'sick':
        return 'warning';
      case 'injured':
        return 'error';
      default:
        return 'primary';
    }
  };

  const getStatusIcon = (status: HealthStatus) => {
    switch (status) {
      case 'normal':
        return '🏃‍➡️';
      case 'sick':
        return '🤒';
      case 'injured':
        return '🧑‍🦽‍➡️';
      default:
        return '❓';
    }
  };

  const getDialogMessage = () => {
    if (!pendingStatus) return '';

    if (pendingStatus === 'sick' || pendingStatus === 'injured') {
      return `Are you sure you want to mark yourself as ${pendingStatus}? Today's workout will be cancelled, and your status will be tracked for recovery planning.`;
    } else {
      return `Are you sure you're ready to return to normal training? Your training plan will be adjusted based on your recovery period.`;
    }
  };

  // Get current sick/injured period info
  const getCurrentPeriodInfo = () => {
    if (currentStatus === 'normal') return null;

    const lastEntry = statusHistory[statusHistory.length - 1];
    if (!lastEntry || lastEntry.endDate) return null;

    const startDate = new Date(lastEntry.startDate);
    const today = new Date();
    const daysDiff = Math.ceil((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    return {
      status: lastEntry.status,
      daysSince: daysDiff,
      startDate: lastEntry.startDate,
    };
  };

  const periodInfo = getCurrentPeriodInfo();

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 20 }}>💪</Typography>
            <Typography variant="h6" fontSize="1.1rem">
              Health Status
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleOpenHistory}
            title="View History"
            aria-label="View health status history"
          >
            <History />
          </IconButton>
        </Box>

        <Box display="flex" gap={1} mb={2}>
          <Button
            variant={currentStatus === 'normal' ? 'contained' : 'outlined'}
            color={getStatusColor('normal')}
            onClick={() => handleStatusClick('normal')}
            disabled={loading}
            startIcon={<span>{getStatusIcon('normal')}</span>}
            sx={{ flex: 1 }}
            aria-label="Mark health status as normal"
          ></Button>
          <Button
            variant={currentStatus === 'sick' ? 'contained' : 'outlined'}
            color={getStatusColor('sick')}
            onClick={() => handleStatusClick('sick')}
            disabled={loading}
            startIcon={<span>{getStatusIcon('sick')}</span>}
            sx={{ flex: 1 }}
            aria-label="Mark health status as sick"
          ></Button>
          <Button
            variant={currentStatus === 'injured' ? 'contained' : 'outlined'}
            color={getStatusColor('injured')}
            onClick={() => handleStatusClick('injured')}
            disabled={loading}
            startIcon={<span>{getStatusIcon('injured')}</span>}
            sx={{ flex: 1 }}
            aria-label="Mark health status as injured"
          ></Button>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {adjustmentInfo && (
          <Alert severity="info" onClose={() => setAdjustmentInfo(null)} sx={{ mb: 2 }}>
            {adjustmentInfo}
          </Alert>
        )}

        {periodInfo && (
          <Box mt={2}>
            <Alert severity="info">
              <Typography variant="body2">
                {periodInfo.status === 'sick' ? '🤒' : '🤕'} You've been {periodInfo.status} for{' '}
                <strong>{periodInfo.daysSince}</strong> day{periodInfo.daysSince !== 1 ? 's' : ''}.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Since: {new Date(periodInfo.startDate).toLocaleDateString()}
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialogOpen} onClose={handleCancelDialog}>
          <DialogTitle>
            {pendingStatus && getStatusIcon(pendingStatus)} Update Health Status
          </DialogTitle>
          <DialogContent>
            <DialogContentText>{getDialogMessage()}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCancelDialog}
              disabled={loading}
              aria-label="Cancel health status change"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmStatusChange}
              color="primary"
              variant="contained"
              disabled={loading}
              autoFocus
              aria-label="Confirm health status change"
            >
              {loading ? <CircularProgress size={24} /> : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* History Modal */}
        <Dialog
          open={historyModalOpen}
          onClose={() => setHistoryModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Health Status History</DialogTitle>
          <DialogContent>
            {refreshingHistory ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                <CircularProgress size={40} />
              </Box>
            ) : statusHistory.length === 0 ? (
              <Typography color="text.secondary">No history available</Typography>
            ) : (
              <List>
                {statusHistory.map((entry, index) => {
                  const isMostRecent = index === statusHistory.length - 1 && !entry.endDate;
                  return (
                    <ListItem
                      key={index}
                      divider={index < statusHistory.length - 1}
                      secondaryAction={
                        isMostRecent ? (
                          <Box>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={handleEditStartDate}
                              title="Edit start date"
                              aria-label="Edit health status start date"
                              sx={{ mr: 1 }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={handleDeleteMostRecent}
                              title="Delete this entry"
                              aria-label="Delete health status entry"
                              color="error"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : null
                      }
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <span>{getStatusIcon(entry.status)}</span>
                            <Typography variant="body1" component="span" textTransform="capitalize">
                              {entry.status}
                            </Typography>
                            {isMostRecent && (
                              <Typography variant="caption" color="primary" component="span">
                                (Current)
                              </Typography>
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" component="span">
                              Start: {new Date(entry.startDate).toLocaleDateString()}
                            </Typography>
                            <br />
                            <Typography variant="body2" component="span">
                              End:{' '}
                              {entry.endDate
                                ? new Date(entry.endDate).toLocaleDateString()
                                : 'Ongoing'}
                            </Typography>
                            {entry.durationDays && (
                              <>
                                <br />
                                <Typography variant="body2" component="span">
                                  Duration: {entry.durationDays} day
                                  {entry.durationDays !== 1 ? 's' : ''}
                                </Typography>
                              </>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setHistoryModalOpen(false)}
              aria-label="Close health status history"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Start Date Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
          <DialogTitle>Edit Start Date</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Change the start date for your current health status.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Start Date"
              type="date"
              fullWidth
              value={editStartDate}
              onChange={e => setEditStartDate(e.target.value)}
              aria-label="Edit health status start date"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: new Date().toISOString().split('T')[0], // Can't be in the future
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setEditDialogOpen(false)}
              disabled={loading}
              aria-label="Cancel editing start date"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmEditStartDate}
              color="primary"
              variant="contained"
              disabled={loading || !editStartDate}
              aria-label="Update health status start date"
            >
              {loading ? <CircularProgress size={24} /> : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Status Entry</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete your current health status entry? This will revert you
              to the previous status.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={loading}
              aria-label="Cancel deleting status entry"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={loading}
              aria-label="Confirm delete status entry"
            >
              {loading ? <CircularProgress size={24} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default HealthStatusWidget;
